import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { isUnauthorized } from '@/lib/api'

const SESSION_KEY = 'session'

/**
 * Turns a 401 on *any* later request back into the signed-out state.
 *
 * A session can end while the app is open — it expires, an Owner removes the
 * member, a role change ends their sessions. The next request then answers 401
 * and the page it was for shows an error, which tells the viewer nothing.
 * Watching the query and mutation caches catches every one of those without
 * each screen having to know about sign-in: the session query is invalidated,
 * refetched, answers 401 in its turn, and the gate above swaps the shell for
 * the signed-out page — with `return_to` still pointing at wherever they were.
 *
 * The session query's own errors are ignored (it would invalidate itself), and
 * nothing happens unless the app currently believes it *is* signed in, so a
 * page full of refused requests cannot start a refetch loop.
 */
export function useSessionExpiry(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    const expire = () => {
      // `find` matches exactly by default; the session query is keyed by client
      // kind as well, so this has to be a prefix match.
      const session = queryClient.getQueryCache().find({ queryKey: [SESSION_KEY], exact: false })
      if (session?.state.status !== 'success') return
      void queryClient.invalidateQueries({ queryKey: [SESSION_KEY] })
    }

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'error') return
      if (event.query.queryKey[0] === SESSION_KEY) return
      if (isUnauthorized(event.action.error)) expire()
    })

    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'error') return
      if (isUnauthorized(event.action.error)) expire()
    })

    return () => {
      unsubscribeQueries()
      unsubscribeMutations()
    }
  }, [queryClient])
}
